import { Node } from "reactflow";
import api from "core/api/api";
import utils from "core/utils";
import { createComponentNode, calculateNodePosition, shouldIncludeComponent } from "./node.utils";
import { createComponentToFlowMapping, createCrossFlowNodes } from "./cross-flow.utils";
import { LAYOUT_CONFIG } from "./flow.constants";
import { loadAlgAndLayout } from "app/components/flow/designer/nodesPositionStorage";
import type { AppDispatch } from "../../../../stores/redux/store";
import {
    setCurrentLayoutAction,
    setPlacementAlgorithmAction
} from "../../../../stores/redux/features/flowdesigner/flowdesigner.slice";

// ============================================================================
// USER PREFERENCES UTILITIES
// ============================================================================

export const fetchUserPreferences = async (appFQN: string, dispatch: AppDispatch) => {
    try {
        const algAndLayout = loadAlgAndLayout(appFQN);
        if (algAndLayout) {
            dispatch(setCurrentLayoutAction(algAndLayout.direction));
            dispatch(setPlacementAlgorithmAction(algAndLayout.algorithm));
        }
    } catch (error) {
        console.error("Error fetching user preferences: ", error);
    }
};

export const getAppComponents = async (appFQN: string) => {
    try {
        const appComponents = await api.getApplicationComponents(appFQN, true);
        return appComponents;
    } catch (error) {
        console.error("Failed to fetch application components:", error);
        return [];
    }
};

// ============================================================================
// FLOW DATA FETCHING UTILITIES
// ============================================================================

/**
 * Fetches and transforms all application components into React Flow nodes for rendering
 * in the flow designer canvas.
 *
 * 1. Get the components from getApplicationComponents API and sort them
 * 2. Find and Add the components that should be displayed based on the view
 * 3. Find and Add the cross-flow components
 *
 */
export const getExistingNodes = async (appFQN: string, appComponents: any[], currentFlowId?: string) => {
    // 1. Calls getApplicationComponents API to retrieve all components for the given `appFQN`
    // 2. Sorts the components by creation time
    appComponents.sort((a: any, b: any) => b.ctime - a.ctime);

    // 3. Builds a mapping of which components belong to which flows so the main app view can hide components
    // that are already encapsulated inside a flow.
    if (appComponents && appComponents.length > 0) {
        const newNodes: Node[] = [];
        let xOffset = LAYOUT_CONFIG.INITIAL_X_OFFSET;
        let yOffset = LAYOUT_CONFIG.INITIAL_Y_OFFSET;
        let componentsInRow = 0;
        // Create a separate array of Flow Components
        const flowComponents = appComponents.filter((component: any) => component.type === utils.ENTITIES.FLOW);
        // Determine if we're viewing a specific flow or the main app view
        const isViewingSpecificFlow: boolean = currentFlowId ? currentFlowId !== appFQN : false;
        // Find the current Flow component
        const selectedFlow = isViewingSpecificFlow
            ? flowComponents.find((flow: any) => flow.id === currentFlowId)
            : null;
        // Get all component IDs that belong to flows to hide them from the main view
        // 1. `componentsInFlows` – a Set of every component ID seen across all flows (and app-level).
        // 2. `componentToFlowMap` – a Map<componentId, flowId | appFQN> used by cross-flow utilities to determine which flow a given component lives in.
        const { componentsInFlows, componentToFlowMap } = createComponentToFlowMapping(
            appComponents,
            flowComponents,
            appFQN
        );

        // `componentsInSelectedFlow` - a Set of every component ID in selected flow
        const componentsInSelectedFlow = new Set<string>();
        if (selectedFlow && selectedFlow.objects) {
            Object.values(selectedFlow.objects).forEach((componentList: any) => {
                if (Array.isArray(componentList)) {
                    componentList.forEach((componentId: string) => {
                        componentsInSelectedFlow.add(componentId);
                    });
                }
            });
        }

        appComponents.forEach((component: any) => {
            // Skip components that should not be shown
            if (
                !shouldIncludeComponent(
                    component,
                    isViewingSpecificFlow,
                    componentsInSelectedFlow,
                    componentsInFlows,
                    componentToFlowMap,
                    appFQN
                )
            ) {
                return;
            }

            // Calculate position
            const { position, newXOffset, newYOffset, newComponentsInRow } = calculateNodePosition(
                componentsInRow,
                xOffset,
                yOffset
            );

            xOffset = newXOffset;
            yOffset = newYOffset;
            componentsInRow = newComponentsInRow;

            // Create the component node
            const componentNode = createComponentNode(component, position);
            newNodes.push(componentNode);
        });

        // If viewing a specific flow, add flow nodes for cross-flow connections
        if (isViewingSpecificFlow && selectedFlow) {
            const crossFlowNodes = await createCrossFlowNodes(
                appComponents,
                flowComponents,
                selectedFlow,
                componentsInSelectedFlow,
                componentToFlowMap,
                xOffset,
                yOffset,
                LAYOUT_CONFIG.HORIZONTAL_SPACING,
                LAYOUT_CONFIG.VERTICAL_SPACING,
                componentsInRow,
                LAYOUT_CONFIG.MAX_COMPONENTS_PER_ROW
            );

            newNodes.push(...crossFlowNodes);
        }

        return newNodes;
    }
};
